import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import AdminLayout from '../../components/admin/AdminLayout';

interface Company {
  company_code: string;
  company_name: string;
  email: string;
  plan: string;
  subscription_status: string;
  created_at: string;
  carrier_count: number;
  rate_con_count: number;
 suspended?: boolean;
trial_ends_at?: string;
}

const AdminDashboardPage: React.FC = () => {
  const [stats, setStats] = useState({
    totalCompanies: 0,
    totalCarriers: 0,
    totalRateCons: 0,
    totalDocuments: 0,
    activeSubscriptions: 0,
  });
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Messaging states
  const [showAdminMsgModal, setShowAdminMsgModal] = useState(false);
  const [adminMsgRecipient, setAdminMsgRecipient] = useState<'all' | 'specific'>('all');
  const [adminMsgTitle, setAdminMsgTitle] = useState('');
  const [adminMsgText, setAdminMsgText] = useState('');
  const [sendingAdminMsg, setSendingAdminMsg] = useState(false);
  const [brokerUsers, setBrokerUsers] = useState<any[]>([]);
  const [selectedBrokerIds, setSelectedBrokerIds] = useState<string[]>([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const { data: brokers } = await supabase
        .from('profiles')
        .select('*')
        .eq('role', 'broker')
        .order('created_at', { ascending: false });

      const { count: carrierCount } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('role', 'carrier');

      const { count: rateConCount } = await supabase
        .from('agreements')
        .select('*', { count: 'exact', head: true });

      const { count: docCount } = await supabase
        .from('documents')
        .select('*', { count: 'exact', head: true });

      const { data: apps } = await supabase
        .from('applications')
        .select('company_code');

      const { data: rateCons } = await supabase
        .from('agreements')
        .select('company_code');

      const companiesList = (brokers || []).map(b => {
        const carrierCt = (apps || []).filter(a => a.company_code === b.company_code).length;
        const rateConCt = (rateCons || []).filter(r => r.company_code === b.company_code).length;
        return {
          company_code: b.company_code,
          company_name: b.company_name || 'Unnamed',
          email: b.email,
          plan: b.plan || 'starter',
          subscription_status: b.subscription_status || 'trial',
          created_at: b.created_at,
          carrier_count: carrierCt,
          rate_con_count: rateConCt,
          suspended: b.suspended || false,
trial_ends_at: b.trial_ends_at || null,
        };
      });

      setCompanies(companiesList);
      setStats({
        totalCompanies: brokers?.length || 0,
        totalCarriers: carrierCount || 0,
        totalRateCons: rateConCount || 0,
        totalDocuments: docCount || 0,
        activeSubscriptions: (brokers || []).filter(b => b.subscription_status === 'active').length,
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuspend = async (companyCode: string, currentlySuspended: boolean) => {
    const action = currentlySuspended ? 'reactivate' : 'suspend';
    if (!confirm(`Are you sure you want to ${action} this company?`)) return;
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ suspended: !currentlySuspended })
        .eq('company_code', companyCode)
        .eq('role', 'broker');
      if (error) throw error;
      setCompanies(prev => prev.map(c =>
        c.company_code === companyCode ? { ...c, suspended: !currentlySuspended } : c
      ));
      if (selectedCompany?.company_code === companyCode) {
        setSelectedCompany(prev => prev ? { ...prev, suspended: !currentlySuspended } : null);
      }
    } catch (err) {
      console.error('Error:', err);
      alert(`Failed to ${action} company.`);
    }
  };

  const fetchBrokerUsers = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('id, first_name, last_name, email, company_name')
      .eq('role', 'broker');
    if (data) setBrokerUsers(data);
  };

  const handleSendAdminMessage = async () => {
    if (sendingAdminMsg) return;
    if (!adminMsgTitle.trim() || !adminMsgText.trim()) {
      alert('Please fill in title and message');
      return;
    }
    setSendingAdminMsg(true);
    try {
      let recipients: string[] = [];
      if (adminMsgRecipient === 'all') {
        const { data } = await supabase.from('profiles').select('id').eq('role', 'broker');
        recipients = data?.map((u: any) => u.id) || [];
      } else {
        recipients = selectedBrokerIds;
      }

      if (recipients.length === 0) {
        alert('No recipients found');
        return;
      }

      const notifications = recipients.map(rid => ({
        recipient_id: rid,
        type: 'admin_message',
        title: adminMsgTitle,
        message: adminMsgText,
        read: false,
        company_code: 'SYSTEM',
      }));

      await supabase.from('notifications').insert(notifications);
      alert(`Message sent to ${recipients.length} broker(s)!`);
      setShowAdminMsgModal(false);
      setAdminMsgTitle('');
      setAdminMsgText('');
      setSelectedBrokerIds([]);
    } catch (err) {
      console.error('Error sending admin message:', err);
      alert('Failed to send message.');
    } finally {
      setSendingAdminMsg(false);
    }
  };

  const filteredCompanies = companies.filter(c => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return c.company_name.toLowerCase().includes(q)
      || c.email.toLowerCase().includes(q)
      || c.company_code.toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center py-12">
          <div className="text-gray-500">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-5">
            <p className="text-sm text-gray-500">Companies</p>
            <p className="text-3xl font-bold text-gray-900">{stats.totalCompanies}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-5">
            <p className="text-sm text-gray-500">Carriers</p>
            <p className="text-3xl font-bold text-blue-600">{stats.totalCarriers}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-5">
            <p className="text-sm text-gray-500">Rate Cons</p>
            <p className="text-3xl font-bold text-blue-600">{stats.totalRateCons}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-5">
            <p className="text-sm text-gray-500">Documents</p>
            <p className="text-3xl font-bold text-green-600">{stats.totalDocuments}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-5">
            <p className="text-sm text-gray-500">Active Subs</p>
            <p className="text-3xl font-bold text-emerald-600">{stats.activeSubscriptions}</p>
          </div>
        </div>

        {/* Search + Send Message Button */}
        <div className="mb-4 flex gap-3">
          <input
            type="text"
            placeholder="Search by company name, email, or code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={() => { setShowAdminMsgModal(true); fetchBrokerUsers(); }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2 whitespace-nowrap"
          >
            ✉️ Message Brokers
          </button>
        </div>

        {/* Companies Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">All Companies</h2>
          </div>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Carriers</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate Cons</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredCompanies.map((company) => (
                <tr key={company.company_code} className={`hover:bg-gray-50 ${company.suspended ? 'bg-red-50' : ''}`}>
                  <td className="px-6 py-4">
                    <p className="text-sm font-semibold text-gray-900">{company.company_name}</p>
                    <p className="text-xs text-gray-500">{company.email}</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-mono text-gray-700">{company.company_code}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs font-semibold px-2 py-1 rounded ${
                      company.plan === 'enterprise' ? 'bg-green-100 text-green-700'
                        : company.plan === 'professional' ? 'bg-blue-100 text-blue-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {company.plan}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {company.suspended ? (
                      <span className="text-xs font-semibold px-2 py-1 rounded bg-red-100 text-red-700">Suspended</span>
                    ) : (
                      <span className={`text-xs font-semibold px-2 py-1 rounded ${
                        company.subscription_status === 'active' ? 'bg-green-100 text-green-700'
                          :(company.subscription_status === 'trial' && (!company.trial_ends_at || new Date(company.trial_ends_at) >= new Date())) ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {company.subscription_status === 'trial' && company.trial_ends_at && new Date(company.trial_ends_at) < new Date() ? 'expired' : company.subscription_status}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">{company.carrier_count}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{company.rate_con_count}</td>
                  <td className="px-6 py-4 text-xs text-gray-500">
                    {new Date(company.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedCompany(company)}
                        className="text-xs px-3 py-1.5 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 font-medium"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleSuspend(company.company_code, company.suspended || false)}
                        className={`text-xs px-3 py-1.5 rounded-md font-medium ${
                          company.suspended
                            ? 'bg-green-50 text-green-700 hover:bg-green-100'
                            : 'bg-red-50 text-red-700 hover:bg-red-100'
                        }`}
                      >
                        {company.suspended ? 'Reactivate' : 'Suspend'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredCompanies.length === 0 && (
            <div className="p-8 text-center text-gray-500">No companies found.</div>
          )}
        </div>

        {/* Company Detail Modal */}
        {selectedCompany && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">{selectedCompany.company_name}</h3>
                <button onClick={() => setSelectedCompany(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="text-sm font-medium">{selectedCompany.email}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Company Code</p>
                  <p className="text-sm font-medium font-mono">{selectedCompany.company_code}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Plan</p>
                  <p className="text-sm font-medium capitalize">{selectedCompany.plan}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <p className={`text-sm font-medium ${selectedCompany.suspended ? 'text-red-600' : 'text-green-600'}`}>
                    {selectedCompany.suspended ? 'Suspended' : selectedCompany.subscription_status}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Carriers</p>
                  <p className="text-sm font-medium">{selectedCompany.carrier_count}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Rate Cons</p>
                  <p className="text-sm font-medium">{selectedCompany.rate_con_count}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Joined</p>
                  <p className="text-sm font-medium">{new Date(selectedCompany.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  onClick={() => handleSuspend(selectedCompany.company_code, selectedCompany.suspended || false)}
                  className={`px-4 py-2 text-sm rounded-md font-medium ${
                    selectedCompany.suspended
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  {selectedCompany.suspended ? 'Reactivate Company' : 'Suspend Company'}
                </button>
                <button onClick={() => setSelectedCompany(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Send Message to Brokers Modal */}
        {showAdminMsgModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h2 className="text-lg font-bold mb-4">Send Message to Brokers</h2>

              <div className="flex gap-4 mb-4">
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" checked={adminMsgRecipient === 'all'} onChange={() => setAdminMsgRecipient('all')} />
                  All Brokers
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" checked={adminMsgRecipient === 'specific'} onChange={() => { setAdminMsgRecipient('specific'); fetchBrokerUsers(); }} />
                  Specific Brokers
                </label>
              </div>

              {adminMsgRecipient === 'specific' && (
                <div className="mb-4 max-h-40 overflow-y-auto border rounded p-2">
                  {brokerUsers.length === 0 ? (
                    <p className="text-xs text-gray-400">Loading brokers...</p>
                  ) : (
                    brokerUsers.map(b => (
                      <label key={b.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer hover:bg-gray-50 px-1 rounded">
                        <input
                          type="checkbox"
                          checked={selectedBrokerIds.includes(b.id)}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedBrokerIds(prev => [...prev, b.id]);
                            else setSelectedBrokerIds(prev => prev.filter(id => id !== b.id));
                          }}
                        />
                        <span>{b.first_name} {b.last_name}</span>
                        <span className="text-xs text-gray-400">({b.company_name || b.email})</span>
                      </label>
                    ))
                  )}
                </div>
              )}

              <input
                type="text"
                placeholder="Title"
                value={adminMsgTitle}
                onChange={e => setAdminMsgTitle(e.target.value)}
                className="w-full border rounded px-3 py-2 mb-3 text-sm"
              />
              <textarea
                placeholder="Message"
                value={adminMsgText}
                onChange={e => setAdminMsgText(e.target.value)}
                rows={4}
                className="w-full border rounded px-3 py-2 mb-4 text-sm"
              />

              <div className="flex justify-end gap-3">
                <button onClick={() => { setShowAdminMsgModal(false); setAdminMsgTitle(''); setAdminMsgText(''); setSelectedBrokerIds([]); }} className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm">Cancel</button>
                <button
                  onClick={handleSendAdminMessage}
                  disabled={sendingAdminMsg || !adminMsgTitle.trim() || !adminMsgText.trim() || (adminMsgRecipient === 'specific' && selectedBrokerIds.length === 0)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50"
                >
                  {sendingAdminMsg ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboardPage;